import { useState, useEffect, useRef } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { Link } from "react-router-dom";
import { useAuth, SignUpButton } from "@clerk/clerk-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import {
  LucideArrowLeft,
  LucideEye,
  LucideEyeOff,
  LucideDownload,
  LucideChevronLeft,
  LucideChevronRight,
  LucideCheck,
  LucideFilePlus,
  LucideUsers,
} from "lucide-react";
import { PDFDocument } from "pdf-lib";
import { fill as fillPDF } from "./fill";
import templateUrl from "./template.pdf";
import { pdfjs } from "react-pdf";

pdfjs.GlobalWorkerOptions.workerSrc = new URL(
  "pdfjs-dist/build/pdf.worker.min.mjs",
  import.meta.url,
).toString();
import { BACKEND_URL } from "@/constants/api";
import { PRESETS } from "./presets";
import { LSWH_COLOR_MAP } from "../shared/colorSystem";
import { PillSelector } from "../shared/PillSelector";
import { PDFPreview } from "../shared/PDFPreview";
import { SectionHeading } from "../shared/SectionHeading";
import { AIGeneratedTextarea } from "../shared/AIGeneratedTextarea";
import { FormFieldGrid } from "../shared/FormFieldGrid";

// One representative PDF field per step — used to read the y-position from the template
const STEP_ANCHOR_FIELDS = {
  1: "studentName1",
  2: "board",
  3: "daysAbsent",
  4: "responsibility",
  5: "languagePWD",
  6: "artsHeader",
};
import { twoPlans, threePlans, fourPlans, sixPlans, threeResults, selectItems } from "./fields";

// LSWH_COLOR_MAP is imported from ../shared/colorSystem
const RESULT_COLOR_MAP = { PWD: "amber", PW: "blue", PVW: "emerald" };

// ─── Step metadata ────────────────────────────────────────────────────────────
const STEPS = [
  { id: 1, title: "Student Info" },
  { id: 2, title: "School Info" },
  { id: 3, title: "Attendance" },
  { id: 4, title: "Learning Skills" },
  { id: 5, title: "Subject Grades" },
  { id: 6, title: "Arts & Comments" },
];

const STEP_FIELDS = {
  1: ["studentName", "oen", "date", "grade", "teacher"],
  2: ["board", "school", "address1", "address2", "principal", "telephone"],
  3: ["daysAbsent", "totalDaysAbsent", "timesLate", "totalTimesLate", "boardInformation1"],
  4: ["responsibility", "organization", "independentWork", "collaboration", "initiative", "selfRegulation", "strengthsNextStepsLSWH"],
  5: [
    "languagePlan", "languageResult",
    "frenchPlan", "frenchResult",
    "nativeLanguage", "nativeLanguagePlan", "nativeLanguageResult",
    "mathematicsPlan", "mathematicsResult",
    "scienceAndTechnologyPlan", "scienceAndTechnologyResult",
    "socialStudiesPlan", "socialStudiesResult",
    "healthEducationPlan", "healthEducationResult",
    "physicalEducationPlan", "physicalEducationResult",
  ],
  6: [
    "artsHeader",
    "arts1", "arts1Plan", "arts1Result",
    "arts2", "arts2Plan", "arts2Result",
    "arts3", "arts3Plan", "arts3Result",
    "arts4", "arts4Plan", "arts4Result",
    "optionalStudies", "optionalStudiesPlan", "optionalStudiesResult",
    "strengthsNextStepsSubjects", "boardInformation2",
  ],
};

const CORE_SUBJECTS = [
  { label: "Language",             planField: "languagePlan",             resultField: "languageResult",             plans: twoPlans },
  { label: "French",               planField: "frenchPlan",               resultField: "frenchResult",               plans: sixPlans },
  { label: "Native Language",      planField: "nativeLanguagePlan",       resultField: "nativeLanguageResult",       plans: twoPlans, nameField: "nativeLanguage" },
  { label: "Mathematics",          planField: "mathematicsPlan",          resultField: "mathematicsResult",          plans: threePlans },
  { label: "Science & Technology", planField: "scienceAndTechnologyPlan", resultField: "scienceAndTechnologyResult", plans: threePlans },
  { label: "Social Studies",       planField: "socialStudiesPlan",        resultField: "socialStudiesResult",        plans: threePlans },
  { label: "Health Education",     planField: "healthEducationPlan",      resultField: "healthEducationResult",      plans: threePlans },
  { label: "Physical Education",   planField: "physicalEducationPlan",    resultField: "physicalEducationResult",    plans: threePlans },
];

const ARTS_ROWS = [
  { nameField: "arts1", planField: "arts1Plan", resultField: "arts1Result", placeholder: "e.g. Visual Arts" },
  { nameField: "arts2", planField: "arts2Plan", resultField: "arts2Result", placeholder: "e.g. Music" },
  { nameField: "arts3", planField: "arts3Plan", resultField: "arts3Result", placeholder: "e.g. Drama" },
  { nameField: "arts4", planField: "arts4Plan", resultField: "arts4Result", placeholder: "e.g. Dance" },
];

// Maps each RHF schema field to one representative PDF field name used to locate
// the widget's bounding box for the yellow highlight drawn directly on the PDF.
const SCHEMA_TO_PDF_FIELD = {
  date: "date", studentName: "studentName1", oen: "oen1", grade: "grade1",
  board: "board", daysAbsent: "daysAbsent", totalDaysAbsent: "totalDaysAbsent",
  teacher: "teacher", timesLate: "timesLate", totalTimesLate: "totalTimesLate",
  school: "school", address1: "address1", address2: "address2",
  principal: "principal", telephone: "telephone", boardInformation1: "boardInformation1",
  responsibility: "responsibility", organization: "organization",
  independentWork: "independentWork", collaboration: "collaboration",
  initiative: "initiative", selfRegulation: "selfRegulation",
  strengthsNextStepsLSWH: "strengthsNextSteps",
  languagePlan: "languageESLELD", languageResult: "languagePWD",
  frenchPlan: "frenchESLELD", frenchResult: "frenchPWD",
  nativeLanguage: "nativeLanguage",
  nativeLanguagePlan: "nativeLanguageESLELD", nativeLanguageResult: "nativeLanguagePWD",
  mathematicsPlan: "mathematicsESLELD", mathematicsResult: "mathematicsPWD",
  scienceAndTechnologyPlan: "scienceAndTechnologyESLELD", scienceAndTechnologyResult: "scienceAndTechnologyPWD",
  socialStudiesPlan: "socialStudiesESLELD", socialStudiesResult: "socialStudiesPWD",
  healthEducationPlan: "healthEducationESLELD", healthEducationResult: "healthEducationPWD",
  physicalEducationPlan: "physicalEducationESLELD", physicalEducationResult: "physicalEducationPWD",
  artsHeader: "artsHeader",
  arts1: "arts1", arts1Plan: "arts1ESLELD", arts1Result: "arts1PWD",
  arts2: "arts2", arts2Plan: "arts2ESLELD", arts2Result: "arts2PWD",
  arts3: "arts3", arts3Plan: "arts3ESLELD", arts3Result: "arts3PWD",
  arts4: "arts4", arts4Plan: "arts4ESLELD", arts4Result: "arts4PWD",
  optionalStudies: "optionalStudies",
  optionalStudiesPlan: "optionalStudiesESLELD", optionalStudiesResult: "optionalStudiesPWD",
  strengthsNextStepsSubjects: "strengthsNextStepsSubjects",
  boardInformation2: "boardInformation2",
};

// Fields that live on page 1 of the PDF (all others are on page 2)
const PAGE_1_SCHEMA_FIELDS = new Set([
  "date", "studentName", "oen", "grade", "board", "daysAbsent", "totalDaysAbsent",
  "teacher", "timesLate", "totalTimesLate", "school", "address1", "address2",
  "principal", "telephone", "boardInformation1",
  "responsibility", "organization", "independentWork", "collaboration",
  "initiative", "selfRegulation", "strengthsNextStepsLSWH",
]);

// ─── Sub-components ───────────────────────────────────────────────────────────
// PillSelector is imported from ../shared/PillSelector

function SubjectRow({ label, planField, resultField, plans, nameField, control }) {
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-4 space-y-3">
      <p className="font-semibold text-slate-800">{label}</p>
      {nameField && (
        <FormField
          control={control}
          name={nameField}
          render={({ field }) => (
            <FormItem>
              <FormControl>
                <Input placeholder={`${label} name`} {...field} />
              </FormControl>
            </FormItem>
          )}
        />
      )}      
      <div className="space-y-1">
        <p className="text-xs text-slate-500 font-medium uppercase tracking-wide">Plan</p>
        <FormField
          control={control}
          name={planField}
          render={({ field }) => (
            <FormItem>
              <FormControl>
                <PillSelector value={field.value} onChange={field.onChange} options={plans} colorMap={{}} />
              </FormControl>
            </FormItem>
          )}
        />
      </div>
      <div className="space-y-1">
        <p className="text-xs text-slate-500 font-medium uppercase tracking-wide">Result</p>
        <FormField
          control={control}
          name={resultField}
          render={({ field }) => (
            <FormItem>
              <FormControl>
                <PillSelector value={field.value} onChange={field.onChange} options={threeResults} colorMap={RESULT_COLOR_MAP} />
              </FormControl>
            </FormItem>
          )}
        />
      </div>
    </div>
  );
}

function ArtsRow({ nameField, planField, resultField, placeholder, control }) {
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-4 space-y-3">
      <FormField
        control={control}
        name={nameField}
        render={({ field }) => (
          <FormItem>
            <FormLabel className="text-xs text-slate-500 font-medium uppercase tracking-wide">Subject Name</FormLabel>
            <FormControl>
              <Input placeholder={placeholder} {...field} />
            </FormControl>
          </FormItem>
        )}
      />
      <div className="space-y-1">
        <p className="text-xs text-slate-500 font-medium uppercase tracking-wide">Plan</p>
        <FormField
          control={control}
          name={planField}
          render={({ field }) => (
            <FormItem>
              <FormControl>
                <PillSelector value={field.value} onChange={field.onChange} options={fourPlans} colorMap={{}} />
              </FormControl>
            </FormItem>
          )}
        />
      </div>
      <div className="space-y-1">
        <p className="text-xs text-slate-500 font-medium uppercase tracking-wide">Result</p>
        <FormField
          control={control}
          name={resultField}
          render={({ field }) => (
            <FormItem>
              <FormControl>
                <PillSelector value={field.value} onChange={field.onChange} options={threeResults} colorMap={RESULT_COLOR_MAP} />
              </FormControl>
            </FormItem>
          )}
        />
      </div>
    </div>
  );
}

function StepIndicator({ steps, currentStep, completedSteps, onStepClick }) {
  return (
    <div className="flex items-start justify-between w-full">
      {steps.map((step, index) => {
        const isDone = completedSteps.has(step.id);
        const isCurrent = step.id === currentStep;
        const isLast = index === steps.length - 1;
        return (
          <div key={step.id} className="flex items-center flex-1">
            <div className="flex flex-col items-center">
              <button
                type="button"
                onClick={() => onStepClick(step.id)}
                className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-semibold border-2 transition-all cursor-pointer ${
                  isDone && !isCurrent
                    ? "bg-teal-700 border-teal-700 text-white hover:bg-teal-600"
                    : isCurrent
                    ? "bg-teal-600 border-teal-600 text-white scale-110 ring-4 ring-inset ring-teal-400/50"
                    : "bg-slate-100 border-slate-200 text-slate-400 hover:bg-slate-200"
                }`}
              >
                {isDone && !isCurrent ? <LucideCheck size={14} /> : step.id}
              </button>
              <span
                className={`text-xs mt-1 text-center leading-tight ${
                  isCurrent ? "text-teal-700 font-bold" : isDone ? "text-teal-600 font-medium" : "text-slate-400 font-medium"
                }`}
              >
                {step.title}
              </span>
            </div>
            {!isLast && (
              <div className={`h-0.5 flex-1 mx-1 mb-4 ${isDone ? "bg-teal-600" : "bg-slate-200"}`} />
            )}
          </div>
        );
      })}
    </div>
  );
}

// ─── Form schema ──────────────────────────────────────────────────────────────
const formSchema = z.object({
  date: z.string(),
  studentName: z.string(),
  oen: z.string(),
  daysAbsent: z.string(),
  totalDaysAbsent: z.string(),
  grade: z.string(),
  teacher: z.string(),
  timesLate: z.string(),
  totalTimesLate: z.string(),
  board: z.string(),
  school: z.string(),
  address1: z.string(),
  address2: z.string(),
  principal: z.string(),
  telephone: z.string(),
  boardInformation1: z.string(),
  responsibility: z.string(),
  organization: z.string(),
  independentWork: z.string(),
  collaboration: z.string(),
  initiative: z.string(),
  selfRegulation: z.string(),
  strengthsNextStepsLSWH: z.string(),
  languagePlan: z.string(),
  languageResult: z.string(),
  frenchPlan: z.string(),
  frenchResult: z.string(),
  nativeLanguage: z.string(),
  nativeLanguagePlan: z.string(),
  nativeLanguageResult: z.string(),
  mathematicsPlan: z.string(),
  mathematicsResult: z.string(),
  scienceAndTechnologyPlan: z.string(),
  scienceAndTechnologyResult: z.string(),
  socialStudiesPlan: z.string(),
  socialStudiesResult: z.string(),
  healthEducationPlan: z.string(),
  healthEducationResult: z.string(),
  physicalEducationPlan: z.string(),
  physicalEducationResult: z.string(),
  artsHeader: z.string(),
  arts1: z.string(),
  arts1Plan: z.string(),
  arts1Result: z.string(),
  arts2: z.string(),
  arts2Plan: z.string(),
  arts2Result: z.string(),
  arts3: z.string(),
  arts3Plan: z.string(),
  arts3Result: z.string(),
  arts4: z.string(),
  arts4Plan: z.string(),
  arts4Result: z.string(),
  optionalStudies: z.string(),
  optionalStudiesPlan: z.string(),
  optionalStudiesResult: z.string(),
  strengthsNextStepsSubjects: z.string(),
  boardInformation2: z.string(),
});

// PDFPreview is imported from ../shared/PDFPreview

// ─── Main component ───────────────────────────────────────────────────────────
export const FreeTrialReportCardProgressV2 = ({ reportId, backUrl, contextInfo } = {}) => {
  const [currentStep, setCurrentStep] = useState(1);
  const [filledPDF, setFilledPDF] = useState(null);
  const [isPdfGenerating, setIsPdfGenerating] = useState(false);
  const [showPreview, setShowPreview] = useState(true);
  const [isGeneratingAI, setIsGeneratingAI] = useState({ lswh: false, subjects: false });
  const [aiError, setAiError] = useState(null);
  const [showCTA, setShowCTA] = useState(false);
  const [lastChangedField, setLastChangedField] = useState(null);
  // scrollTargets[stepId] = { page: 1|2, topFraction: 0–1 } — fraction from top of page
  const [scrollTargets, setScrollTargets] = useState(
    Object.fromEntries(STEPS.map((s) => [s.id, { page: s.id <= 4 ? 1 : 2, topFraction: 0 }]))
  );
  const debounceRef = useRef(null);
  const regenDebounceRef = useRef(null);
  const fieldPositionsRef = useRef({});   // schemaField → { pageIndex, x, y, width, height }
  const lastChangedFieldRef = useRef(null);
  const { getToken } = useAuth();

  const form = useForm({
    resolver: zodResolver(formSchema),
    defaultValues: Object.fromEntries(Object.keys(formSchema.shape).map((k) => [k, ""])),
  });

  // Load saved data on mount + generate initial PDF
  useEffect(() => {
    let initialValues;
    if (reportId) {
      getToken().then((token) =>
        fetch(`${BACKEND_URL}api/reports/${reportId}`, {
          headers: { Authorization: `Bearer ${token}` },
        })
          .then((r) => r.json())
          .then((json) => {
            const values = json.data && Object.keys(json.data).length > 0 ? json.data : form.getValues();
            form.reset(values);
            fillPDF(values).then((url) => setFilledPDF(url));
          })
          .catch(() => fillPDF(form.getValues()).then((url) => setFilledPDF(url)))
      );
      return;
    }
    const saved = localStorage.getItem("formData-pgrc-v2");
    if (saved) {
      try {
        const parsed = JSON.parse(saved);
        form.reset(parsed);
        initialValues = parsed;
      } catch {
        initialValues = form.getValues();
      }
    } else {
      initialValues = form.getValues();
    }
    fillPDF(initialValues).then((url) => setFilledPDF(url));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Build per-step scroll targets by inspecting field positions in the PDF template
  useEffect(() => {
    async function buildTargets() {
      try {
        const bytes = await fetch(templateUrl).then((r) => r.arrayBuffer());
        const pdfDoc = await PDFDocument.load(bytes);
        const pdfForm = pdfDoc.getForm();
        const pages = pdfDoc.getPages();

        const updated = {};
        for (const [stepStr, fieldName] of Object.entries(STEP_ANCHOR_FIELDS)) {
          const step = Number(stepStr);
          const pageIdx = step <= 4 ? 0 : 1;
          try {
            const field = pdfForm.getField(fieldName);
            const widgets = field.acroField.getWidgets();
            if (!widgets.length) throw new Error("no widgets");
            const rect = widgets[0].getRectangle();
            const pageH = pages[pageIdx].getHeight();
            // rect.y is from bottom in PDF pts; convert to 0–1 fraction from top (with 40pt padding)
            const topFromTop = Math.max(0, pageH - rect.y - rect.height - 40);
            const topFraction = Math.min(0.95, topFromTop / pageH);
            updated[step] = { page: pageIdx + 1, topFraction };
          } catch {
            updated[step] = { page: pageIdx + 1, topFraction: 0 };
          }
        }
        setScrollTargets((prev) => ({ ...prev, ...updated }));

        // Build per-field bounding boxes for yellow PDF highlight
        const positions = {};
        for (const [schemaField, pdfFieldName] of Object.entries(SCHEMA_TO_PDF_FIELD)) {
          const pageIdx = PAGE_1_SCHEMA_FIELDS.has(schemaField) ? 0 : 1;
          try {
            const f = pdfForm.getField(pdfFieldName);
            const widgets = f.acroField.getWidgets();
            if (!widgets.length) continue;
            const rect = widgets[0].getRectangle();
            positions[schemaField] = { pageIndex: pageIdx, x: rect.x, y: rect.y, width: rect.width, height: rect.height };
          } catch { /* field not in template, skip */ }
        }
        fieldPositionsRef.current = positions;
      } catch (e) {
        console.error("Failed to build PDF scroll targets:", e);
      }
    }
    buildTargets();
  }, []);

  // Auto-save, field highlight, and live PDF regeneration
  useEffect(() => {
    const subscription = form.watch((_, { name }) => {
      // Auto-save (300 ms)
      if (debounceRef.current) clearTimeout(debounceRef.current);
      debounceRef.current = setTimeout(() => {
        if (reportId) {
          getToken().then((token) =>
            fetch(`${BACKEND_URL}api/reports/${reportId}`, {
              method: "PUT",
              headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
              body: JSON.stringify({ data: form.getValues() }),
            })
          );
        } else {
          localStorage.setItem("formData-pgrc-v2", JSON.stringify(form.getValues()));
        }
      }, 300);

      // Track last changed field (state for UI, ref for async callback)
      if (name) {
        setLastChangedField(name);
        lastChangedFieldRef.current = name;
      }

      // Live PDF regeneration (500 ms debounce so we don't regen while typing)
      if (regenDebounceRef.current) clearTimeout(regenDebounceRef.current);
      setIsPdfGenerating(true);
      regenDebounceRef.current = setTimeout(async () => {
        const changedField = lastChangedFieldRef.current;
        const highlightRect = changedField ? (fieldPositionsRef.current[changedField] ?? null) : null;
        const url = await fillPDF(form.getValues(), highlightRect);
        setFilledPDF(url);
        setIsPdfGenerating(false);
      }, 500);
    });
    return () => {
      subscription.unsubscribe();
      if (debounceRef.current) clearTimeout(debounceRef.current);
      if (regenDebounceRef.current) clearTimeout(regenDebounceRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form]);

  const handleGenerateReportCard = async () => {
    const url = await fillPDF(form.getValues());
    setFilledPDF(url);
    setShowPreview(true);
  };

  const handleDownload = () => {
    if (!filledPDF) return;
    const link = document.createElement("a");
    link.href = filledPDF;
    link.setAttribute("download", "report-card.pdf");
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    if (!reportId) setShowCTA(true);
  };

  const handleNext = () => setCurrentStep((s) => Math.min(s + 1, STEPS.length));

  const handlePrev = () => setCurrentStep((s) => Math.max(s - 1, 1));

  const handleGenerateAI = async (fieldName, aiKey) => {
    setAiError(null);
    const token = await getToken();
    if (!token) {
      setAiError("Sign in to use AI comment generation.");
      return;
    }
    setIsGeneratingAI((prev) => ({ ...prev, [aiKey]: true }));
    try {
      const values = form.getValues();
      const response = await fetch(`${BACKEND_URL}api/report-comments`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          reportId: reportId || "free-trial",
          strengths: values[fieldName] || "",
          nextSteps: values[fieldName] || "",
          studentInfo: {
            grade: values.grade,
            absent: values.daysAbsent,
            late: values.timesLate,
            responsibility: values.responsibility,
            organized: values.organization,
            independent: values.independentWork,
            collaboration: values.collaboration,
            initiative: values.initiative,
          },
        }),
      });
      if (response.status === 403) {
        setAiError("AI generation quota exceeded. Please upgrade your plan.");
        return;
      }
      if (!response.ok) {
        setAiError("AI generation requires a paid plan. Visit the pricing page to upgrade.");
        return;
      }
      const { comment } = await response.json();
      form.setValue(fieldName, comment);
    } catch {
      setAiError("Failed to generate AI comment. Please try again.");
    } finally {
      setIsGeneratingAI((prev) => ({ ...prev, [aiKey]: false }));
    }
  };

  // ── Step completion (all fields non-empty) ───────────────────────────────────
  const watchedValues = form.watch();
  const completedSteps = new Set(
    STEPS.filter((step) =>
      STEP_FIELDS[step.id].every((f) => watchedValues[f] && watchedValues[f] !== "")
    ).map((step) => step.id)
  );

  // ── Step content ────────────────────────────────────────────────────────────
  const hl = () => "";
  const pillHl = () => "rounded-lg";

  const renderStep = () => {
    const { control } = form;

    if (currentStep === 1) {
      return (
        <div className="space-y-4">
          <SectionHeading title="Student Information" />
          <FormFieldGrid
            fields={[
              { name: "studentName", label: "Student Name" },
              { name: "oen",         label: "OEN" },
              { name: "date",        label: "Date" },
              { name: "grade",       label: "Grade" },
              { name: "teacher",     label: "Teacher" },
            ]}
            control={control}
          />
        </div>
      );
    }

    if (currentStep === 2) {
      return (
        <div className="space-y-4">
          <SectionHeading title="School Information" />
          <FormFieldGrid
            fields={[
              { name: "board",     label: "Board" },
              { name: "school",    label: "School" },
              { name: "address1",  label: "Address Line 1" },
              { name: "address2",  label: "Address Line 2" },
              { name: "principal", label: "Principal" },
              { name: "telephone", label: "Telephone" },
            ]}
            control={control}
          />
        </div>
      );
    }

    if (currentStep === 3) {
      return (
        <div className="space-y-4">
          <SectionHeading title="Attendance" />
          <FormFieldGrid
            fields={[
              { name: "daysAbsent",      label: "Days Absent (this term)", inputMode: "numeric" },
              { name: "totalDaysAbsent", label: "Total Days Absent",        inputMode: "numeric" },
              { name: "timesLate",       label: "Times Late (this term)",   inputMode: "numeric" },
              { name: "totalTimesLate",  label: "Total Times Late",         inputMode: "numeric" },
            ]}
            control={control}
            gridClassName="grid grid-cols-2 gap-6"
          />
          <FormField
            control={control}
            name="boardInformation1"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Board Information (Page 1)</FormLabel>
                <FormControl>
                  <Textarea {...field} className={`min-h-[80px] ${hl("boardInformation1")}`} />
                </FormControl>
              </FormItem>
            )}
          />
        </div>
      );
    }

    if (currentStep === 4) {
      const SKILL_LABELS = {
        responsibility:  "Responsibility",
        organization:    "Organization",
        independentWork: "Independent Work",
        collaboration:   "Collaboration",
        initiative:      "Initiative",
        selfRegulation:  "Self-Regulation",
      };
      return (
        <div className="space-y-5">
          <SectionHeading title="Learning Skills & Work Habits" />
          <div className="grid grid-cols-2 gap-4">
            {Object.entries(SKILL_LABELS).map(([name, label]) => (
              <div key={name} className="rounded-xl border border-slate-200 bg-white p-4 space-y-2">
                <p className="font-semibold text-slate-700 text-sm">{label}</p>
                <FormField
                  control={control}
                  name={name}
                  render={({ field }) => (
                    <FormItem>
                      <FormControl>
                        <PillSelector
                          value={field.value}
                          onChange={field.onChange}
                          options={selectItems}
                          colorMap={LSWH_COLOR_MAP}
                        />
                      </FormControl>
                    </FormItem>
                  )}
                />
              </div>
            ))}
          </div>
          <AIGeneratedTextarea
            control={control}
            name="strengthsNextStepsLSWH"
            label="Strengths & Next Steps — Learning Skills"
            isGenerating={isGeneratingAI.lswh}
            onGenerate={() => handleGenerateAI("strengthsNextStepsLSWH", "lswh")}
          />
          {aiError && <p className="text-sm text-red-600 mt-1">{aiError}</p>}
        </div>
      );
    }

    if (currentStep === 5) {
      return (
        <div className="space-y-4">
          <SectionHeading title="Subject Grades" />
          <div className="space-y-3">
            {CORE_SUBJECTS.map((subject) => (
              <SubjectRow key={subject.planField} control={control} {...subject} />
            ))}
          </div>
        </div>
      );
    }

    if (currentStep === 6) {
      return (
        <div className="space-y-5">
          <SectionHeading title="Arts & Final Comments" />

          <FormField
            control={control}
            name="artsHeader"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Arts Header</FormLabel>
                <FormControl>
                  <Input placeholder="e.g. The Arts" {...field} className={hl("artsHeader")} />
                </FormControl>
              </FormItem>
            )}
          />

          <div>
            <p className="text-sm font-semibold text-slate-600 mb-2">Arts Subjects</p>
            <div className="space-y-3">
              {ARTS_ROWS.map((row) => (
                <ArtsRow key={row.nameField} control={control} {...row} />
              ))}
            </div>
          </div>

          <div className="rounded-xl border border-slate-200 bg-white p-4 space-y-3">
            <p className="font-semibold text-slate-800">Optional Studies</p>
            <FormField
              control={control}
              name="optionalStudies"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="text-xs text-slate-500 font-medium uppercase tracking-wide">Subject Name</FormLabel>
                  <FormControl>
                    <Input placeholder="e.g. Islamic Studies" {...field} className={hl("optionalStudies")} />
                  </FormControl>
                </FormItem>
              )}
            />
            <div className={`space-y-1 ${pillHl("optionalStudiesPlan")}`}>
              <p className="text-xs text-slate-500 font-medium uppercase tracking-wide">Plan</p>
              <FormField
                control={control}
                name="optionalStudiesPlan"
                render={({ field }) => (
                  <FormItem>
                    <FormControl>
                      <PillSelector value={field.value} onChange={field.onChange} options={fourPlans} colorMap={{}} />
                    </FormControl>
                  </FormItem>
                )}
              />
            </div>
            <div className={`space-y-1 ${pillHl("optionalStudiesResult")}`}>
              <p className="text-xs text-slate-500 font-medium uppercase tracking-wide">Result</p>
              <FormField
                control={control}
                name="optionalStudiesResult"
                render={({ field }) => (
                  <FormItem>
                    <FormControl>
                      <PillSelector value={field.value} onChange={field.onChange} options={threeResults} colorMap={RESULT_COLOR_MAP} />
                    </FormControl>
                  </FormItem>
                )}
              />
            </div>
          </div>

          <AIGeneratedTextarea
            control={control}
            name="strengthsNextStepsSubjects"
            label="Strengths & Next Steps — Subjects"
            isGenerating={isGeneratingAI.subjects}
            onGenerate={() => handleGenerateAI("strengthsNextStepsSubjects", "subjects")}
          />
          {aiError && <p className="text-sm text-red-600 mt-1">{aiError}</p>}

          <FormField
            control={control}
            name="boardInformation2"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Board Information (Page 2)</FormLabel>
                <FormControl>
                  <Textarea {...field} className={`min-h-[80px] ${hl("boardInformation2")}`} />
                </FormControl>
              </FormItem>
            )}
          />
        </div>
      );
    }

    return null;
  };

  const isShared = backUrl?.startsWith("/shared-classes");

  // ── Render ──────────────────────────────────────────────────────────────────
  return (
    <div className="h-[calc(100vh-4rem)] flex flex-col" style={{ fontFamily: "'DM Sans', sans-serif" }}>
      {/* Header bar */}
      <div className="flex items-center justify-between px-5 py-3 border-b border-slate-200 bg-white shrink-0">
        <Link to={backUrl || "/"}>
          <Button variant="ghost" size="sm">
            <LucideArrowLeft size={16} className="mr-1.5" />
            Back
          </Button>
        </Link>
        {contextInfo ? (
          <div className="text-center">
            <p className="text-xs text-slate-500 leading-none">
              {[contextInfo.grade && `Grade ${contextInfo.grade}`, contextInfo.schoolYear]
                .filter(Boolean).join(' · ')}
            </p>
            <p className="text-sm font-semibold text-slate-800 leading-tight mt-0.5">
              {[contextInfo.oen && `OEN ${contextInfo.oen}`, contextInfo.studentName]
                .filter(Boolean).join(' · ')}
            </p>
          </div>
        ) : (
          <h1
            className="text-lg font-semibold text-slate-800"
            style={{ fontFamily: "'DM Serif Display', serif" }}
          >
            Ontario Progress Report Card
          </h1>
        )}
        <div className="flex items-center gap-2">
          <Button variant="ghost" size="sm" onClick={() => setShowPreview((v) => !v)}>
            {showPreview ? <LucideEyeOff size={16} /> : <LucideEye size={16} />}
          </Button>
          <Button variant="outline" size="sm" onClick={handleDownload} disabled={!filledPDF || isPdfGenerating}>
            {isPdfGenerating ? <span className="text-xs">Updating…</span> : <LucideDownload size={16} />}
          </Button>
        </div>
      </div>

      {/* Shared-class banner */}
      {isShared && (
        <div className="flex items-center gap-2 px-5 py-1.5 bg-amber-50 border-b border-amber-200 text-xs text-amber-700 shrink-0">
          <LucideUsers size={12} className="shrink-0" />
          Shared class — changes you save are visible to the owner
        </div>
      )}

      {/* Main content */}
      <div className="flex flex-1 overflow-hidden">
        {/* Form panel */}
        <div
          className={`flex flex-col overflow-hidden transition-all duration-300 ${
            showPreview ? "w-[55%]" : "w-full"
          }`}
        >
          {/* Step indicator */}
          <div className="px-6 pt-5 pb-3 shrink-0">
            <StepIndicator
              steps={STEPS}
              currentStep={currentStep}
              completedSteps={completedSteps}
              onStepClick={setCurrentStep}
            />
          </div>

          {/* Free-trial preset toolbar */}
          {!reportId && (
            <div className="px-6 py-2 shrink-0 flex flex-wrap gap-2 border-b border-slate-100 bg-slate-50">
              {PRESETS.map((preset) => (
                <Button
                  key={preset.label}
                  type="button"
                  variant="outline"
                  size="sm"
                  className="text-xs transition-all duration-150 hover:scale-105 hover:shadow-md active:scale-95"
                  onClick={() => form.reset(preset.data)}
                >
                  {preset.label}
                </Button>
              ))}
            </div>
          )}

          {/* Step content */}
          <div className="flex-1 overflow-y-auto px-6 pb-4">
            <Form {...form}>
              <form>
                <div className="rounded-2xl border border-slate-200 bg-white shadow-sm p-6">
                  {renderStep()}
                </div>
              </form>
            </Form>
          </div>

          {/* Navigation buttons */}
          <div className="flex items-center justify-between px-6 py-3 border-t border-slate-100 bg-white shrink-0">
            <Button
              type="button"
              variant="outline"
              onClick={handlePrev}
              disabled={currentStep === 1}
            >
              <LucideChevronLeft size={16} className="mr-1" />
              Prev
            </Button>

            {/* Generate PDF button hidden — PDF auto-fills on every change
            <Button
              type="button"
              variant="outline"
              onClick={handleGenerateReportCard}
              className="border-teal-600 text-teal-700 hover:bg-teal-50"
            >
              <LucideFilePlus size={16} className="mr-1.5" />
              Generate PDF
            </Button>
            */}

            {currentStep < STEPS.length ? (
              <Button type="button" onClick={handleNext}>
                Next
                <LucideChevronRight size={16} className="ml-1" />
              </Button>
            ) : (
              <Button type="button" onClick={handleDownload} disabled={!filledPDF}>
                <LucideDownload size={16} className="mr-1.5" />
                Download PDF
              </Button>
            )}
          </div>
        </div>

        {/* PDF preview panel */}
        <div
          className={`border-l border-slate-200 transition-all duration-300 overflow-hidden ${
            showPreview ? "w-[45%]" : "w-0"
          }`}
        >
          {showPreview && (
            <PDFPreview
              src={filledPDF}
              scrollTarget={scrollTargets[currentStep]}
              defaultPageCount={2}
            />
          )}
        </div>
      </div>

      {/* Sign-up CTA overlay — shown after first free-trial download */}
      {showCTA && (
        <div className="fixed inset-0 bg-black/50 flex items-end justify-center z-50 p-4">
          <div className="bg-white rounded-t-xl p-6 max-w-md w-full space-y-4">
            <h2 className="text-xl font-semibold text-slate-800">Your report card is ready!</h2>
            <p className="text-slate-600 text-sm">
              Sign up free to save this report, add more students, and access it from any device.
            </p>
            <SignUpButton mode="modal">
              <Button className="w-full">Sign Up Free</Button>
            </SignUpButton>
            <Button
              variant="ghost"
              className="w-full text-slate-500"
              onClick={() => setShowCTA(false)}
            >
              Continue without saving
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};
